import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { useDataTable } from '@/hooks/use-data-table';
import { getFilterOperators } from '@/lib/data-table';
import { formatDate } from '@/lib/format';
import { getFiltersStateParser, getSortingStateParser } from '@/lib/parsers';
import { useGetTransits } from '@/services/apis/queries/transits/use-get-transits';
import type { Transit } from '@/types/core/transit/transit';
import type { ExtendedColumnSort } from '@/types/data-table';
import { DdsStatusBadge } from '../dds-status-badge';
import { TransitStatusBadge } from '../transit-status-badge';
import { TRANSIT_STATUS_VARIANTS } from '../transit-status-config';

const COLUMN_IDS = ['name', 'createdAt', 'status'];
const DEFAULT_SORTING: ExtendedColumnSort<Transit>[] = [
  { id: 'createdAt', desc: true },
];

export function useTransitsTable() {
  const { t, i18n } = useTranslation('transits');
  const locale = i18n.language;
  const [page] = useQueryState('page', parseAsInteger.withDefault(1));
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [sort] = useQueryState(
    'sort',
    getSortingStateParser<Transit>(COLUMN_IDS).withDefault(DEFAULT_SORTING),
  );
  const [filters] = useQueryState(
    'filters',
    getFiltersStateParser<Transit>(COLUMN_IDS).withDefault([]),
  );
  const query = useGetTransits({ page, perPage, sort, filters });
  const columns = useMemo<ColumnDef<Transit>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        size: 260,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('name')} />
        ),
        enableHiding: false,
        enableColumnFilter: true,
        meta: {
          label: t('name'),
          variant: 'text',
          enableColumnReordering: false,
        },
        cell: ({ row }) => (
          <Link
            className='text-primary underline hover:text-primary/80'
            params={{ id: String(row.original.id) }}
            search={true}
            to='/transits/$id'
          >
            {row.original.name || row.original.id}
          </Link>
        ),
      },
      {
        id: 'status',
        accessorFn: (row) => row.status.id,
        size: 420,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('statusLabel')} />
        ),
        enableHiding: false,
        enableColumnFilter: true,
        meta: {
          label: t('statusLabel'),
          variant: 'multiSelect',
          options: Object.keys(TRANSIT_STATUS_VARIANTS).map((id) => ({
            label: t(`status.${id}`, { defaultValue: t('unknownStatus') }),
            value: id,
          })),
          enableColumnReordering: false,
        },
        cell: ({ row }) => <TransitStatusBadge status={row.original.status} />,
      },
      {
        id: 'ddsStatus',
        accessorKey: 'ddsStatus',
        size: 300,
        header: t('tracesStatusLabel'),
        enableHiding: false,
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          label: t('tracesStatusLabel'),
          enableColumnReordering: false,
        },
        cell: ({ row }) => <DdsStatusBadge status={row.original.ddsStatus} />,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        size: 240,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('createdAt')} />
        ),
        cell: ({ row }) => formatDate(row.original.createdAt, { locale }),
        enableHiding: false,
        enableColumnFilter: true,
        meta: {
          label: t('createdAt'),
          variant: 'date',
          filterOperators: getFilterOperators('date').filter(({ value }) =>
            ['eq', 'lt', 'gte', 'isBetween'].includes(value),
          ),
          enableColumnReordering: false,
        },
      },
    ],
    [t, locale],
  );
  const { table } = useDataTable({
    data: query.data?.data ?? [],
    columns,
    pageCount: query.data?.maxPage ?? 0,
    initialState: { sorting: DEFAULT_SORTING },
    enableAdvancedFilter: true,
    enableRowSelection: false,
  });

  return { table, query };
}
